// Core
import { CSSProperties, ReactNode } from 'react';
// Packages
import { Menu as AntMenu, MenuProps } from 'antd';
import { placements } from '@rc-component/menu/lib/placements';
import { RenderIconInfo, MenuInfo, SelectInfo } from '@rc-component/menu/lib/interface';

const { Item, SubMenu, ItemGroup } = AntMenu;

export type TMenuInfo = MenuInfo;
export type TMenuItem = NonNullable<MenuProps['items']>[number];

interface IPlacement {
  points: string[];
  offset?: number[];
  overflow: {
    adjustX: number;
    adjustY: number;
    alwaysByViewport: boolean;
  };
}

// rc-menu measures the room left for a submenu popup against the whole document instead of the
// visible viewport. On any page wide or tall enough to scroll, the space past the screen edge
// still counts as "room", so a popup that overflows is never flipped to the other side — it is
// simply drawn off screen and cut off (the settings gear sits in the top right corner, so its
// menu opened rightwards into nothing). `alwaysByViewport` limits that measurement to the
// viewport, which is what makes rc-align flip the popup and keep it fully visible.
const viewportAwarePlacements = Object.keys(placements).reduce<Record<string, IPlacement>>(
  (acc, key) => {
    const { overflow, ...placement } = placements[key as keyof typeof placements];

    return {
      ...acc,
      [key]: { ...placement, overflow: { ...overflow, alwaysByViewport: true } },
    };
  },
  {},
);

interface IMenuProps {
  className?: string;
  defaultOpenKeys?: string[];
  defaultSelectedKeys?: string[];
  disabledOverflow?: boolean;
  expandIcon?: ReactNode | ((props: RenderIconInfo) => React.ReactNode);
  forceSubMenuRender?: boolean;
  items?: MenuProps['items'];
  inlineCollapsed?: boolean;
  inlineIndent?: number;
  mode?: 'vertical' | 'horizontal' | 'inline';
  multiple?: boolean;
  openKeys?: string[];
  overflowedIndicator?: ReactNode;
  selectable?: boolean;
  selectedKeys?: string[];
  style?: CSSProperties;
  subMenuCloseDelay?: number;
  subMenuOpenDelay?: number;
  theme?: 'light' | 'dark';
  triggerSubMenuAction?: 'hover' | 'click';
  onClick?: (info: MenuInfo) => void;
  onDeselect?: (info: SelectInfo) => void;
  onOpenChange?: (openKeys: React.Key[]) => void;
  onSelect?: (info: SelectInfo) => void;
  children?: ReactNode;
}

/**
 * A versatile menu for navigation.
 *
 * @param className - Container className
 * @param defaultOpenKeys - Array with the keys of default opened sub menus
 * @param defaultSelectedKeys - Array with the keys of default selected menu items
 * @param disabledOverflow - Disable the horizontal responsive overflow indicator. Use it when
 *   every top level item must stay visible, otherwise a collapsed menu can never expand back
 * @param expandIcon - custom expand icon of submenu
 * @param forceSubMenuRender - Render submenu into DOM before it becomes visible
 * @param items - Menu items (antd 6 replaces JSX children with this)
 * @param inlineCollapsed - Show only the icons of an inline menu (sub menus open as popups)
 * @param inlineIndent - Indent (in pixels) of inline menu items on each level
 * @param mode - Type of menu
 * @param multiple - Allows selection of multiple items
 * @param openKeys - Array with the keys of currently opened sub-menus
 * @param overflowedIndicator - Customized icon when menu is collapsed
 * @param selectable - Allows selecting menu items
 * @param selectedKeys - Array with the keys of currently selected menu items
 * @param style - Style of the root node
 * @param subMenuCloseDelay - Delay time to hide submenu when mouse leaves (in seconds)
 * @param subMenuOpenDelay - Delay time to show submenu when mouse enters, (in seconds)
 * @param theme - Color theme of the menu
 * @param triggerSubMenuAction - Which action can trigger submenu open/close
 * @param onClick - Called when a menu item is clicked
 * @param onDeselect - Called when a menu item is deselected (multiple mode only)
 * @param onOpenChange - Called when sub-menus are opened or closed
 * @param onSelect - Called when a menu item is selected
 * @param children - Some ReactNode
 */

const Menu = ({
  className,
  children,
  defaultOpenKeys,
  defaultSelectedKeys,
  disabledOverflow = false,
  expandIcon,
  forceSubMenuRender = false,
  items,
  inlineCollapsed,
  inlineIndent = 24,
  mode = 'vertical',
  multiple = false,
  openKeys,
  overflowedIndicator,
  selectable = true,
  selectedKeys,
  style,
  subMenuCloseDelay = 0.1,
  subMenuOpenDelay = 0,
  theme = 'light',
  triggerSubMenuAction = 'hover',
  onClick,
  onDeselect,
  onOpenChange,
  onSelect,
}: IMenuProps) => (
  <AntMenu
    builtinPlacements={viewportAwarePlacements as unknown as MenuProps['builtinPlacements']}
    className={className}
    defaultOpenKeys={defaultOpenKeys}
    defaultSelectedKeys={defaultSelectedKeys}
    disabledOverflow={disabledOverflow}
    expandIcon={expandIcon}
    forceSubMenuRender={forceSubMenuRender}
    items={items}
    // antd warns about the prop on any non-inline menu, even when it is undefined
    {...(inlineCollapsed !== undefined && { inlineCollapsed })}
    inlineIndent={inlineIndent}
    mode={mode}
    multiple={multiple}
    openKeys={openKeys}
    overflowedIndicator={overflowedIndicator}
    selectable={selectable}
    selectedKeys={selectedKeys}
    style={style}
    subMenuCloseDelay={subMenuCloseDelay}
    subMenuOpenDelay={subMenuOpenDelay}
    theme={theme}
    triggerSubMenuAction={triggerSubMenuAction}
    onClick={onClick}
    onDeselect={onDeselect}
    onOpenChange={onOpenChange}
    onSelect={onSelect}
  >
    {children}
  </AntMenu>
);

Menu.Item = Item;
Menu.SubMenu = SubMenu;
Menu.ItemGroup = ItemGroup;

export default Menu;
